/**
 * Vultisig SDK integration service.
 *
 * Manages SDK lifecycle (init, vault creation/import) and exposes
 * helpers that the rest of the app can use without touching the SDK directly.
 */

import '../polyfills';
import { sha256 as nobleSha256 } from '@noble/hashes/sha256';
import { ripemd160 as nobleRipemd160 } from '@noble/hashes/ripemd160';
import { Vultisig, MemoryStorage, Chain as VultisigChain, SecureVault } from '@vultisig/sdk';
import initDkls, {
	SignSession as DklsSignSession,
	Keyshare as DklsKeyshare,
	KeyExportSession as DklsKeyExportSession,
} from '@vultisig/dkls-wasm';
import initSchnorr, {
	SignSession as SchnorrSignSession,
	Keyshare as SchnorrKeyshare,
} from '@vultisig/schnorr-wasm';
import { unpackVultisigOrchardKeyshareBundle } from '../components/toolbox/zcash-extensions/frozt/orchardFrostBridge';
import { installInMemoryMpcRelay } from './inMemoryMpcRelay';
import {
	isWrappedVault as _isWrappedVault,
	wrapVaultWithFrost as _wrapVaultWithFrost,
	unwrapVault as _unwrapVault,
} from './vaultWrapperFormat';

let sdkInstance = null;
let initPromise = null;

/**
 * Per-window vault state lives in the dependency-free
 * `vaultScope.js` module — see the comments there for the launcher
 * → sub-window parent-chain model. Re-exporting the public surface
 * keeps existing imports of `clearVaultScope`,
 * `registerWindowParent`, … working unchanged.
 */
import {
	getScope as _getScope,
	clearVaultScope as _clearVaultScope,
	getVaultScopeIds as _getVaultScopeIds,
	registerWindowParent as _registerWindowParent,
	unregisterWindowParent as _unregisterWindowParent,
	findActiveVault as _findActiveVaultAnyScope,
} from './vaultScope.js';

export const clearVaultScope = _clearVaultScope;
export const getVaultScopeIds = _getVaultScopeIds;
export const registerWindowParent = _registerWindowParent;
export const unregisterWindowParent = _unregisterWindowParent;

// Maps doritokit chain IDs (BTC, ETH, etc) to Vultisig SDK chain identifiers.
// Use exported enum values when available to avoid version-specific string drift.
const VULTISIG_CHAIN_MAP = {
	BTC:   VultisigChain?.Bitcoin || 'bitcoin',
	ETH:   VultisigChain?.Ethereum || 'ethereum',
	BSC:   VultisigChain?.BSC || 'smartChain',
	AVAX:  VultisigChain?.Avalanche || 'avalancheCChain',
	ARB:   VultisigChain?.Arbitrum || 'arbitrum',
	OP:    VultisigChain?.Optimism || 'optimism',
	MATIC: VultisigChain?.Polygon || 'polygon',
	BASE:  VultisigChain?.Base || 'base',
	SOL:   VultisigChain?.Solana || 'solana',
	THOR:  VultisigChain?.THORChain || 'thorchain',
	MAYA:  VultisigChain?.MayaChain || 'thorchain',
	GAIA:  VultisigChain?.Cosmos || 'cosmos',
	// KUJI:  VultisigChain?.Kujira || 'kujira',  // Disabled — Rujira is a THORChain app layer
	LTC:   VultisigChain?.Litecoin || 'litecoin',
	DOGE:  VultisigChain?.Dogecoin || 'dogecoin',
	DASH:  VultisigChain?.Dash || 'dash',
	BCH:   VultisigChain?.['Bitcoin-Cash'] || VultisigChain?.BitcoinCash || 'bitcoinCash',
	DOT:   VultisigChain?.Polkadot || 'polkadot',
	ZEC:   VultisigChain?.Zcash || 'zcash',
};

const CHAIN_ALIASES = {
	BTC: ['bitcoin', 'Bitcoin'],
	ETH: ['ethereum', 'Ethereum'],
	BSC: ['smartChain', 'BSC'],
	AVAX: ['avalancheCChain', 'Avalanche'],
	ARB: ['arbitrum', 'Arbitrum'],
	OP: ['optimism', 'Optimism'],
	MATIC: ['polygon', 'Polygon'],
	BASE: ['base', 'Base'],
	SOL: ['solana', 'Solana'],
	THOR: ['thorchain', 'THORChain'],
	MAYA: ['thorchain', 'MayaChain'],
	GAIA: ['cosmos', 'Cosmos'],
	// KUJI: ['kujira', 'Rujira'],  // Disabled — Rujira is a THORChain app layer
	LTC: ['litecoin', 'Litecoin'],
	DOGE: ['dogecoin', 'Dogecoin'],
	DASH: ['dash', 'Dash'],
	BCH: ['bitcoinCash', 'Bitcoin-Cash', 'BitcoinCash'],
	DOT: ['polkadot', 'Polkadot'],
	ZEC: ['zcash', 'Zcash'],
};

function uniq(values) {
	return [...new Set(values.filter(Boolean))];
}

export function resolveVultisigChainCandidates(appChain) {
	const key = String(appChain || '').toUpperCase();
	return uniq([
		VULTISIG_CHAIN_MAP[key],
		...(CHAIN_ALIASES[key] || []),
		appChain,
		typeof appChain === 'string' ? appChain.toLowerCase() : null,
	]);
}

export function resolveVultisigChain(appChain) {
	return resolveVultisigChainCandidates(appChain)[0];
}

export function getSDK() {
	if (!sdkInstance) {
		sdkInstance = new Vultisig({ storage: new MemoryStorage() });
	}
	return sdkInstance;
}

export async function initSDK() {
	if (initPromise) return initPromise;

	initPromise = (async () => {
		const sdk = getSDK();
		await sdk.initialize();
		return sdk;
	})();

	try {
		return await initPromise;
	} catch (err) {
		initPromise = null;
		throw new Error(`Vultisig SDK failed to initialise: ${err.message}`);
	}
}

export function getActiveVault(windowId) {
	return _findActiveVaultAnyScope(windowId).vault;
}

/**
 * Return the active vault's compressed ECDSA public key (hex, no 0x prefix).
 * This key is deterministic across all shares of the same vault.
 */
export function getVaultEcdsaPublicKey(windowId) {
	const v = _findActiveVaultAnyScope(windowId).vault;
	if (!v) return null;
	return v.publicKeys?.ecdsa || v.data?.publicKeys?.ecdsa || null;
}

/**
 * Return the active vault's Ed25519 (EdDSA) public key (hex, no 0x prefix).
 * Used for Radix and other Ed25519 chains. The vault's EdDSA key shares
 * are used for MPC signing — the full private key is never reconstructed.
 */
export function getVaultEddsaPublicKey(windowId) {
	const v = _findActiveVaultAnyScope(windowId).vault;
	if (!v) return null;
	return v.publicKeys?.eddsa
		|| v.data?.publicKeys?.eddsa
		|| null;
}

/**
 * Return account-level vault identity for deterministic cross-chain derivation.
 * Uses hexChainCode (BIP-32 chain code) which is:
 *   - the same across ALL shares of the vault
 *   - survives share regeneration (reshare)
 *   - never published on-chain
 *
 * Enforces the vault's signing threshold — for secure vaults, multiple
 * shares must be loaded (matching the MPC threshold) before the derived
 * spending key can be produced.
 *
 * @param {string} [chainLabel='Zcash'] - Human-readable chain name for error messages
 */
/**
 * Return vault identity for deterministic cross-chain ADDRESS derivation.
 *
 * Always returns hexChainCode + publicKeyHex when available, regardless of
 * how many shares are loaded. Address derivation is a public-key-only
 * operation — knowing the address does not grant spending ability.
 *
 * This mirrors Vultisig's own approach: addresses are derived from the
 * vault's BIP-32 public key at the chain's derivation path (e.g.
 * m/44'/133'/0'/0/0 for Zcash). See vultisig-go/address/zcash.go.
 *
 * SPENDING is a separate concern — callers must use vault.sign() (MPC
 * co-sign) or localMPCSign() for any transaction signing.
 */
/**
 * Read cross-chain identity (hex chain code + ECDSA pubkey) from vault fields
 * or from a prior in-memory / file recovery (`_winbit32RecoveredIdentity`).
 *
 * @returns {{ hexChainCode: string, publicKeyHex: string } | null}
 */
export function pickCrossChainIdentityFromVaultObject(v) {
	if (!v) return null;
	const bridging = v._winbit32RecoveredIdentity;
	const hexChainCode = v.hexChainCode
		|| v.data?.hexChainCode
		|| v.keys?.hexChainCode
		|| v.coreVault?.hexChainCode
		|| bridging?.hexChainCode
		|| null;
	const publicKeyHex = v.publicKeys?.ecdsa
		|| v.data?.publicKeys?.ecdsa
		|| bridging?.publicKeyHex
		|| null;
	if (!hexChainCode || !publicKeyHex) return null;
	return { hexChainCode, publicKeyHex };
}

/**
 * Recover vault identity from the local DKLS ECDSA key share (same chain code
 * and group public key on every device). Does **not** require a second share.
 *
 * @returns {Promise<{ hexChainCode: string, publicKeyHex: string } | null>}
 */
export async function recoverEcdsaIdentityFromVaultKeyshare(vault) {
	if (!vault) return null;

	const toHex = (u8) => Array.from(u8).map(b => b.toString(16).padStart(2, '0')).join('');

	// 0. Cheapest path: the SDK exposes the chain code + ECDSA pubkey via
	//    getters once the vault metadata is populated (immutable identity,
	//    fixed at DKG). No WASM, works even while the key shares are still
	//    encrypted/locked.
	try {
		const ccGetter = vault.hexChainCode || vault.data?.hexChainCode
			|| vault.keys?.hexChainCode || vault.coreVault?.hexChainCode || '';
		const pkGetter = vault.publicKeys?.ecdsa || vault.data?.publicKeys?.ecdsa || '';
		if (ccGetter && pkGetter) {
			return { hexChainCode: String(ccGetter), publicKeyHex: String(pkGetter) };
		}
	} catch { /* fall through to key-share recovery */ }

	// 1. Ensure the (possibly encrypted) key shares are decrypted into
	//    coreVault. A freshly-DKG'd vault already holds them in memory, so
	//    don't bail if this throws (it wants a password to *decrypt*).
	if (typeof vault.ensureKeySharesLoaded === 'function') {
		try {
			await vault.ensureKeySharesLoaded();
		} catch (err) {
			console.warn('[recoverEcdsaIdentityFromVaultKeyshare] ensureKeySharesLoaded:', err?.message || err);
		}
	}

	// 2. The ECDSA key share can be a base64 string (imported / exported
	//    vaults), a raw Uint8Array, or — for a freshly-created vault — a
	//    live DKLS `Keyshare` WASM handle. Normalise all three so we can
	//    read the root chain code + group public key. The original code
	//    only accepted the base64 string, which is why `.wult` files
	//    created in-browser shipped WITHOUT cross-chain identity (the
	//    fresh share is a live object, not a string) and then could not
	//    derive Zcash / Monero addresses on reload.
	const ks = vault?.coreVault?.keyShares?.ecdsa
		?? vault?.keyShares?.ecdsa
		?? vault?.data?.keyShares?.ecdsa
		?? null;
	if (!ks) return null;

	let share = null;
	let ownShare = false; // only free shares we deserialised ourselves
	try {
		await initDkls();
		if (typeof ks === 'string') {
			share = DklsKeyshare.fromBytes(Uint8Array.from(atob(ks), c => c.charCodeAt(0)));
			ownShare = true;
		} else if (ks instanceof Uint8Array) {
			share = DklsKeyshare.fromBytes(ks);
			ownShare = true;
		} else if (typeof ks?.rootChainCode === 'function' && typeof ks?.publicKey === 'function') {
			// Live Keyshare handle owned by the SDK — use directly, never free.
			share = ks;
			ownShare = false;
		} else if (typeof ks?.toBytes === 'function') {
			share = DklsKeyshare.fromBytes(ks.toBytes());
			ownShare = true;
		} else {
			return null;
		}
		const cc = share.rootChainCode();
		const pk = share.publicKey();
		return { hexChainCode: toHex(cc), publicKeyHex: toHex(pk) };
	} catch (err) {
		console.warn('[recoverEcdsaIdentityFromVaultKeyshare] failed:', err?.message || err);
		return null;
	} finally {
		if (ownShare && share) {
			try { share.free(); } catch { /* ignore */ }
		}
	}
}

/**
 * If the vault backup omits `hex_chain_code` / ECDSA pubkey in metadata, derive
 * them from the decrypted key share and cache on the vault instance.
 *
 * @returns {Promise<boolean>} true if identity is now available
 */
export async function hydrateVaultCrossChainIdentity(vault) {
	if (!vault) return false;
	if (pickCrossChainIdentityFromVaultObject(vault)) return true;
	const rec = await recoverEcdsaIdentityFromVaultKeyshare(vault);
	if (!rec) return false;
	Object.defineProperty(vault, '_winbit32RecoveredIdentity', {
		value: { hexChainCode: rec.hexChainCode, publicKeyHex: rec.publicKeyHex },
		writable: true,
		enumerable: false,
		configurable: true,
	});
	return true;
}

export async function hydrateVaultCrossChainIdentityForWindow(windowId) {
	const { vault } = _findActiveVaultAnyScope(windowId);
	if (!vault) return false;
	return hydrateVaultCrossChainIdentity(vault);
}

export function getVaultIdentityForCrossChainDerivation(chainLabel = 'Zcash', windowId) {
	const { vault } = _findActiveVaultAnyScope(windowId);
	if (!vault) return null;
	const picked = pickCrossChainIdentityFromVaultObject(vault);
	if (!picked) return null;
	return { hexChainCode: picked.hexChainCode, publicKeyHex: picked.publicKeyHex };
}

/**
 * Check whether the vault has enough shares loaded for local MPC signing.
 * Uses cross-scope lookup so callers (e.g. Zcash wizard) find the vault
 * even when running under a different windowId than the Money Manager.
 */
export function canSpendLocally(windowId) {
	const { vault: v, windowId: resolvedId } = _findActiveVaultAnyScope(windowId);
	if (!v) return false;
	const vaultType = v.data?.type || v.type || 'secure';
	if (vaultType !== 'secure') return true;
	const signers = v.data?.signers || v.signers || [];
	const signersCount = Array.isArray(signers) ? signers.length : 0;
	const configuredThreshold = Number(
		v.data?.threshold || v.threshold || 0
	);
	const threshold = configuredThreshold >= 2
		? configuredThreshold
		: Math.max(2, signersCount >= 2 ? signersCount - 1 : 2);
	return getLoadedVaultCount(resolvedId) >= threshold;
}

/**
 * Stable vault identifier for keying local storage (e.g. frozt bundles).
 * Uses cross-scope lookup.
 */
export function getVaultUid(windowId) {
	const { vault } = _findActiveVaultAnyScope(windowId);
	if (!vault) return null;
	const pk = vault.publicKeys?.ecdsa || vault.data?.publicKeys?.ecdsa || null;
	return pk ? pk.replace(/^0x/i, '').slice(0, 16) : null;
}

/** Backwards-compatible alias used by existing Zcash callers. */
export function getVaultIdentityForZcash(windowId) {
	return getVaultIdentityForCrossChainDerivation('Zcash', windowId);
}

/**
 * Mirror of `getVaultIdentityForZcash` for the Monero side of the
 * Privacy Suite. Same identity material (chain code + public key) is
 * returned — the HKDF salt/info differs per-chain so derived seeds
 * cannot collide. Kept as a separate export so callers self-document
 * which derivation they're about to perform.
 */
export function getVaultIdentityForMonero(windowId) {
	return getVaultIdentityForCrossChainDerivation('Monero', windowId);
}

/**
 * Return the Monero FROST bundle for the active vault, if one is
 * available. Looks at:
 *   1. the in-memory `_moneroFrostBundle` property attached during
 *      vault import / creation,
 *   2. the localStorage cache keyed by vault UID (via
 *      `fromtFrostVaultIntegration.loadFromtBundle`).
 *
 * Returns null when no bundle is reachable. Dynamic import is used
 * for the localStorage helper because `vultisig.js` is loaded on the
 * critical path and the Monero module pulls fromt-wasm with it.
 *
 * NOTE: This is intentionally async — the underlying loader is
 * sync today but importing the module is not. Keeping the contract
 * async means we can swap in IndexedDB later without rewriting
 * every caller.
 *
 * @param {string} [windowId]
 * @returns {Promise<object|null>}
 */
export async function getMoneroFrostBundleForActiveVault(windowId) {
	const { vault } = _findActiveVaultAnyScope(windowId);
	if (!vault) return null;
	if (vault._moneroFrostBundle) return vault._moneroFrostBundle;
	const uid = getVaultUid(windowId);
	if (!uid) return null;
	try {
		const { loadFromtBundle } = await import(
			'../components/toolbox/monero-extensions/fromt/fromtFrostVaultIntegration'
		);
		const bundle = loadFromtBundle(uid) || null;
		if (bundle) {
			Object.defineProperty(vault, '_moneroFrostBundle', {
				value: bundle,
				writable: true,
				enumerable: false,
				configurable: true,
			});
		}
		return bundle;
	} catch (e) {
		console.warn('[vultisig] getMoneroFrostBundleForActiveVault: load failed:', e?.message || e);
		return null;
	}
}

/**
 * Derive a cross-chain seed via mobile QR co-sign using vault.sign().
 *
 * WORKAROUND: vault.signBytes() is broken on the mobile app because
 * it generates a payload-less QR (see vultisig-android#3284).  Instead
 * we call vault.prepareSendTx() to create a minimal "0 ETH to self"
 * keysignPayload that the mobile can parse, then swap the messageHashes
 * with our derivation hash before calling vault.sign().  The MPC
 * protocol signs whatever messageHashes the initiating device provides.
 *
 * If this approach fails (e.g. mobile rejects mismatched hashes),
 * callers should fall back to hexChainCode-based derivation.
 *
 * @param {string} chainLabel - e.g. 'Zcash', 'Radix'
 * @returns {Promise<{signatureHex: string, publicKeyHex: string}>}
 */
export async function deriveViaCoSign(chainLabel = 'Zcash', windowId) {
	const vault = getActiveVault(windowId);
	if (!vault) throw new Error('No vault loaded');

	const publicKeyHex = getVaultEcdsaPublicKey(windowId);
	if (!publicKeyHex) throw new Error('Vault has no ECDSA public key');

	if (typeof vault.sign !== 'function') {
		throw new Error(
			'This vault does not support relay signing. ' +
			'Load a second .vult share file instead.'
		);
	}

	const domainMessage = `WINBIT32-DERIVE-v1:${chainLabel.toLowerCase()}:${publicKeyHex}`;
	const msgBytes = new TextEncoder().encode(domainMessage);
	const hashBuffer = await crypto.subtle.digest('SHA-256', msgBytes);
	const hashHex = bytesToHex(new Uint8Array(hashBuffer));

	const showModal = getSigningModalCallback(windowId);
	const abortController = new AbortController();
	const cancelHandler = () => {
		abortController.abort();
		if (showModal) showModal(null);
	};

	if (showModal) showModal({
		status: 'preparing',
		chain: chainLabel,
		viewOnly: true,
		allowLoadShare: true,
		onCancel: cancelHandler,
	});

	try {
		const ethChain = VultisigChain?.Ethereum || 'Ethereum';
		const ethAddress = await vault.address(ethChain);

		const payload = await vault.prepareSendTx({
			coin: {
				chain: ethChain,
				ticker: 'ETH',
				address: ethAddress,
				decimals: 18,
				isNativeToken: true,
			},
			receiver: ethAddress,
			amount: '0',
			memo: `WINBIT32 derive ${chainLabel}`,
		});

		payload.messageHashes = [hashHex];

		let activeQrPayload = null;
		const signingOpts = {
			signal: abortController.signal,
			onQRCodeReady: (qrPayload) => {
				activeQrPayload = qrPayload;
				if (showModal) showModal({
					status: 'waiting_for_device',
					qrPayload,
					devicesJoined: 0,
					devicesNeeded: 2,
					chain: chainLabel,
					viewOnly: true,
					allowLoadShare: true,
					onCancel: cancelHandler,
				});
			},
			onDeviceJoined: (deviceId, total, required) => {
				if (showModal) showModal({
					status: total >= required ? 'signing' : 'waiting_for_device',
					qrPayload: activeQrPayload,
					devicesJoined: total,
					devicesNeeded: required,
					chain: chainLabel,
					viewOnly: true,
					allowLoadShare: true,
					onCancel: cancelHandler,
				});
			},
		};

		const result = await vault.sign(payload, signingOpts);
		if (showModal) showModal(null);

		if (!result?.signature) {
			throw new Error('Signing completed but no signature returned.');
		}

		const sigHex = typeof result.signature === 'string'
			? (result.signature.startsWith('0x') ? result.signature.slice(2) : result.signature)
			: bytesToHex(new Uint8Array(result.signature));

		return { signatureHex: sigHex, publicKeyHex };
	} catch (err) {
		if (showModal) showModal(null);
		if (abortController.signal.aborted) {
			throw new Error('Co-sign cancelled by user');
		}
		throw new Error(
			`Co-sign failed: ${err.message}`
		);
	}
}

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
function base58Encode(bytes) {
	let num = 0n;
	for (const b of bytes) num = num * 256n + BigInt(b);
	let encoded = '';
	while (num > 0n) {
		encoded = BASE58_ALPHABET[Number(num % 58n)] + encoded;
		num = num / 58n;
	}
	for (const b of bytes) {
		if (b !== 0) break;
		encoded = '1' + encoded;
	}
	return encoded;
}

/**
 * Derive the Zcash transparent address from the vault's BIP-32 derived
 * public key at m/44'/133'/0'/0/0.
 * Uses the same encoding as the Vultisig Go recovery tool:
 *   Hash160(compressedPubKey) + version bytes [0x1C, 0xB8] + checksum → base58
 */
export function zcashTransparentAddressFromVault(vault) {
	if (!vault) return null;

	const cachedTransparent = String(vault?._winbit32ZcashTransparentAddress || '').trim();
	if (cachedTransparent && cachedTransparent.startsWith('t')) {
		return cachedTransparent;
	}

	const chainPubKeys = vault.data?.chainPublicKeys
		|| vault.chainPublicKeys
		|| vault.coreVault?.chainPublicKeys
		|| {};

	const zcashPubKeyHex = chainPubKeys['zcash']
		|| chainPubKeys['ZEC']
		|| chainPubKeys['Zcash']
		|| chainPubKeys['133']
		|| null;

	if (!zcashPubKeyHex) return null;

	try {
		const pubKeyBytes = hexToBytes(zcashPubKeyHex);
		if (pubKeyBytes.length < 33) return null;

		// Hash160 = RIPEMD160(SHA256(pubkey))
		const hash160 = nobleRipemd160(nobleSha256(pubKeyBytes));

		// Zcash transparent P2PKH version bytes [0x1C, 0xB8] for "t1" addresses
		const versioned = new Uint8Array(2 + 20);
		versioned[0] = 0x1C;
		versioned[1] = 0xB8;
		versioned.set(hash160, 2);

		// Double SHA-256 checksum
		const checksum = nobleSha256(nobleSha256(versioned)).slice(0, 4);

		const payload = new Uint8Array(versioned.length + 4);
		payload.set(versioned);
		payload.set(checksum, versioned.length);
		const address = base58Encode(payload);
		if (!address || !address.startsWith('t1')) return null;
		vault._winbit32ZcashTransparentAddress = address;
		return address;
	} catch {
		return null;
	}
}

export function getVaultZcashTransparentAddress(windowId) {
	const _av = _findActiveVaultAnyScope(windowId).vault;
	return zcashTransparentAddressFromVault(_av);
}

/**
 * Return the vault's compressed secp256k1 public key (hex) for ZEC transparent,
 * derived at m/44'/133'/0'/0/0 during DKG.
 */
export function getVaultZcashPublicKeyHex(windowId) {
	const _av = _findActiveVaultAnyScope(windowId).vault;
	if (!_av) return null;
	const chainPubKeys = _av.data?.chainPublicKeys
		|| _av.chainPublicKeys
		|| _av.coreVault?.chainPublicKeys
		|| {};
	const allKeys = Object.keys(chainPubKeys);
	const zcashKey = allKeys.find(k => /^(zcash|zec|133)$/i.test(k));
	return (zcashKey ? chainPubKeys[zcashKey] : null)
		|| chainPubKeys['zcash']
		|| chainPubKeys['ZEC']
		|| chainPubKeys['Zcash']
		|| chainPubKeys['133']
		|| null;
}

/**
 * Ensure a transparent Zcash address is available for the active vault.
 * First tries deterministic local derivation from chain pubkeys; if that
 * is unavailable, falls back to the SDK's `vault.address()` resolution.
 */
export async function ensureVaultZcashTransparentAddress(windowId) {
	const localDerived = getVaultZcashTransparentAddress(windowId);
	if (localDerived) return localDerived;
	const _av = _findActiveVaultAnyScope(windowId).vault;
	if (!_av || typeof _av.address !== 'function') return '';

	const chainCandidates = resolveVultisigChainCandidates('ZEC');
	for (const candidate of chainCandidates) {
		try {
			const addr = String(await _av.address(candidate)).trim();
			if (addr.startsWith('t')) {
				_av._winbit32ZcashTransparentAddress = addr;
				return addr;
			}
		} catch {
			// try next candidate
		}
	}

	if (typeof _av.addresses === 'function') {
		try {
			const all = await _av.addresses();
			const zAddr = String(
				all?.zcash
				|| all?.Zcash
				|| all?.ZEC
				|| all?.[String(VULTISIG_CHAIN_MAP.ZEC)]
				|| ''
			).trim();
			if (zAddr.startsWith('t')) {
				_av._winbit32ZcashTransparentAddress = zAddr;
				return zAddr;
			}
		} catch {
			// ignore fallback failure
		}
	}

	return '';
}

export function setActiveVault(vault, windowId) {
	const scope = _getScope(windowId);
	if (vault === scope.activeVault) return;
	patchSecureVaultSignBytes(vault);

	const normPk = (v) => {
		const raw = v?.publicKeys?.ecdsa || v?.data?.publicKeys?.ecdsa || '';
		return raw.replace(/^0x/i, '').toLowerCase();
	};

	const incomingPartyId = String(vault?.coreVault?.localPartyId || '').trim();

	if (scope.activeVault && vault) {
		const activePk = normPk(scope.activeVault);
		const incomingPk = normPk(vault);

		if (activePk && incomingPk && activePk === incomingPk) {
			const activePartyId = String(scope.activeVault?.coreVault?.localPartyId || '').trim();

			if (incomingPartyId && activePartyId && incomingPartyId === activePartyId) {
				console.debug('[setActiveVault] Same party, replacing active vault (scope=%s)', windowId);
				scope.activeVault = vault;
				return;
			}
			console.debug('[setActiveVault] Same vault group, adding as additional:', incomingPartyId);
			try { addAdditionalVault(vault, windowId); }
			catch { replaceAdditionalVault(vault, windowId); }
			console.debug('[setActiveVault] Count after add:', getLoadedVaultCount(windowId));
			return;
		}
	}

	const prevAdditional = scope.additionalVaults;
	scope.activeVault = vault;
	scope.additionalVaults = [];
	console.debug('[setActiveVault] New vault group, party:', incomingPartyId, 'scope:', windowId);

	if (vault && prevAdditional.length > 0) {
		const newPk = normPk(vault);
		for (const prev of prevAdditional) {
			const prevPk = normPk(prev);
			if (newPk && prevPk && newPk === prevPk) {
				try { addAdditionalVault(prev, windowId); }
				catch { /* already loaded or same party */ }
			}
		}
	}
	console.debug('[setActiveVault] Final count:', getLoadedVaultCount(windowId));
}

export function clearActiveVault(windowId) {
	const scope = _getScope(windowId);
	scope.activeVault = null;
	scope.additionalVaults = [];
}

export function getAdditionalVaults(windowId) {
	return _getScope(windowId).additionalVaults;
}

export function addAdditionalVault(vault, windowId) {
	if (!vault) {
		throw new Error('No vault share provided.');
	}
	const scope = _getScope(windowId);

	const nextPartyId = String(vault?.coreVault?.localPartyId || '').trim();
	if (!nextPartyId) {
		throw new Error('Vault share has no party identifier — file may be corrupt.');
	}

	const activePartyId = String(scope.activeVault?.coreVault?.localPartyId || '').trim();

	if (activePartyId && nextPartyId === activePartyId) {
		throw new Error('This vault share is already loaded as the primary share.');
	}

	const alreadyLoaded = scope.additionalVaults.some((loaded) => {
		const loadedPartyId = String(loaded?.coreVault?.localPartyId || '').trim();
		return loadedPartyId && nextPartyId === loadedPartyId;
	});
	if (alreadyLoaded) {
		throw new Error('This vault share file is already loaded.');
	}

	patchSecureVaultSignBytes(vault);
	scope.additionalVaults.push(vault);
	return true;
}

/**
 * Replace an already-loaded additional vault share by its localPartyId,
 * or add it if no matching share exists yet.
 */
export function replaceAdditionalVault(vault, windowId) {
	if (!vault) throw new Error('No vault share provided.');
	const scope = _getScope(windowId);
	const nextPartyId = String(vault?.coreVault?.localPartyId || '').trim();
	const idx = scope.additionalVaults.findIndex((loaded) => {
		const loadedPartyId = String(loaded?.coreVault?.localPartyId || '').trim();
		return nextPartyId && loadedPartyId && nextPartyId === loadedPartyId;
	});
	if (idx >= 0) {
		scope.additionalVaults[idx] = vault;
	} else {
		scope.additionalVaults.push(vault);
	}
	return true;
}

export function getLoadedVaultCount(windowId) {
	const { windowId: resolvedId } = _findActiveVaultAnyScope(windowId);
	const scope = _getScope(resolvedId);
	return (scope.activeVault ? 1 : 0) + scope.additionalVaults.length;
}

export function getLoadedVaultDebugInfo(windowId) {
	const { windowId: resolvedId } = _findActiveVaultAnyScope(windowId);
	const scope = _getScope(resolvedId);
	return {
		hasActive: Boolean(scope.activeVault),
		activePartyId: scope.activeVault?.coreVault?.localPartyId || '(none)',
		additionalCount: scope.additionalVaults.length,
		additionalPartyIds: scope.additionalVaults.map(v => v?.coreVault?.localPartyId || '?'),
		totalCount: (scope.activeVault ? 1 : 0) + scope.additionalVaults.length,
		resolvedScope: resolvedId,
	};
}

/**
 * Register a callback that opens the signing QR modal.
 * Called from the React component tree (Winbit32.jsx).
 * Signature: (signingState) => void
 */
export function setSigningModalCallback(cb, windowId) {
	_getScope(windowId).signingModalCallback = cb;
}

export function getSigningModalCallback(windowId) {
	const local = _getScope(windowId).signingModalCallback;
	if (local) return local;
	// Fall back to any registered scope's modal callback so a
	// MPC-relay sign request triggered outside the active window
	// still reaches the Money Manager's modal. The scope module
	// keeps the live registry — `getVaultScopeIds` skips the
	// default bucket so we don't double-count.
	for (const id of getVaultScopeIds()) {
		const cb = _getScope(id).signingModalCallback;
		if (cb) return cb;
	}
	return null;
}

/**
 * Decide whether a vault share file needs a password to import.
 *
 * `tryImportNativeVault` returns `null` (rather than throwing a
 * password-specific error) when an encrypted share is opened without
 * the right password, so callers cannot discover "needs password" by
 * catching a thrown error. This lets a loader prompt up front instead —
 * which is what makes a password-protected `.vult` / `.wult` actually
 * ask for its password in standalone (non-Money-Manager) flows.
 *
 * `.wult` wrappers record their own `encrypted` flag, so those are
 * resolved without touching the SDK/WASM at all; raw `.vult` strings
 * fall back to the SDK's `isVaultEncrypted`.
 *
 * @param {string} content  raw file text
 * @returns {Promise<boolean>}
 */
export async function isVaultFileEncrypted(content) {
	if (typeof content !== 'string' || !content) return false;
	try {
		if (isWrappedVault(content)) {
			let inner = null;
			try {
				const obj = JSON.parse(content);
				if (typeof obj?.encrypted === 'boolean') return obj.encrypted;
				inner = typeof obj?.vaultData === 'string' ? obj.vaultData : null;
			} catch { /* fall through */ }
			if (inner) {
				const sdk = await initSDK();
				return sdk.isVaultEncrypted(inner);
			}
			return false;
		}
		const sdk = await initSDK();
		return sdk.isVaultEncrypted(content);
	} catch (err) {
		console.warn('[isVaultFileEncrypted] detection failed:', err?.message || err);
		return false;
	}
}

/**
 * Try to import a .vult file as a native Vultisig vault via the SDK.
 * Returns the vault instance on success, or null if content is not
 * a native Vultisig vault.
 *
 * V2 vaults carry embedded Orchard FROST key material.  When present
 * the returned vault gains a non-enumerable `_frostData` property:
 *   { orchardFrost: { myShare, publicKeyPackage } | null }
 */
export async function tryImportNativeVault(vultContent, password) {
	let sdkString = vultContent;
	let frostData = null;
	let fileCrossIdentity = null;
	let fileSaplingB64 = null;
	let fileMoneroBundle = null;

	try {
		if (isWrappedVault(vultContent)) {
			const unwrapped = await unwrapVault(vultContent, password);
			sdkString = unwrapped.sdkVaultString;
			if (unwrapped.orchardFrost) {
				frostData = { orchardFrost: unwrapped.orchardFrost };
			}
			if (unwrapped.crossChainIdentity) {
				fileCrossIdentity = unwrapped.crossChainIdentity;
			}
			if (unwrapped.saplingBundleBase64) {
				fileSaplingB64 = unwrapped.saplingBundleBase64;
			}
			if (unwrapped.moneroFrost) {
				fileMoneroBundle = unwrapped.moneroFrost;
			}
		}
	} catch (err) {
		console.warn('[tryImportNativeVault] Unwrap failed:', err?.message || err);
		return null;
	}

	const importWith = async (sdk) => {
		const isEncrypted = sdk.isVaultEncrypted(sdkString);
		return sdk.importVault(sdkString, isEncrypted ? password : undefined);
	};

	let vault = null;
	try {
		const sdk = await initSDK();
		vault = await importWith(sdk);
	} catch (err) {
		console.warn('[tryImportNativeVault] Shared SDK import failed, trying fresh instance:', err?.message || err);
		try {
			const freshSdk = new Vultisig({ storage: new MemoryStorage() });
			await freshSdk.initialize();
			vault = await importWith(freshSdk);
		} catch (freshErr) {
			console.warn('[tryImportNativeVault] Fresh SDK import also failed:', freshErr?.message || freshErr);
			return null;
		}
	}

	if (!vault) {
		console.warn('[tryImportNativeVault] importVault returned falsy');
		return null;
	}

	console.debug('[tryImportNativeVault] Imported vault, party:', vault?.coreVault?.localPartyId || '(unknown)');
	patchSecureVaultSignBytes(vault);

	if (!frostData) {
		const nativeOrchardBundle = vault?.coreVault?.chainKeyShares?.[VULTISIG_ORCHARD_CHAIN]
			|| vault?.data?.chainKeyShares?.[VULTISIG_ORCHARD_CHAIN];
		if (nativeOrchardBundle) {
			try {
				const orchardFrost = await unpackVultisigOrchardKeyshareBundle(nativeOrchardBundle);
				frostData = { orchardFrost };
				console.debug('[tryImportNativeVault] Loaded native Vultisig Orchard frozto bundle.');
			} catch (err) {
				console.warn('[tryImportNativeVault] Native Orchard frozto bundle parse failed:', err?.message || err);
			}
		}
	}

	// Native Monero (frosty-lib `Monero` chain) keyshare. Frosty-lib's
	// `KeyShareBundle` binary layout matches what `fromtFrostBridge`
	// expects, so we read the bytes straight out of `chainKeyShares`
	// and surface them as a `MoneroKeyShareBundle` for callers / the
	// localStorage cache. Falls back to the wult `frost.monero` field
	// (already in `fileMoneroBundle`) when the SDK has no `Monero`
	// entry yet.
	if (!fileMoneroBundle) {
		const nativeMonero = vault?.coreVault?.chainKeyShares?.[VULTISIG_MONERO_CHAIN]
			|| vault?.data?.chainKeyShares?.[VULTISIG_MONERO_CHAIN];
		if (nativeMonero) {
			try {
				const bytes = _moneroNativeBundleToBytes(nativeMonero);
				const { parseKeyShareBundleHeader } = await import(
					'../components/toolbox/monero-extensions/fromt/fromtFrostBridge'
				);
				const header = parseKeyShareBundleHeader(bytes);
				const partyId = vault?.coreVault?.localPartyId || vault?.data?.localPartyId || 'native';
				fileMoneroBundle = {
					partyName: partyId,
					frostId: 0,
					network: _networkNameFromByte(header.networkByte),
					birthday: header.birthday,
					bundleBytes: bytes,
					bakedAtCreation: false,
					ceremonyVersion: header.version,
				};
				console.debug('[tryImportNativeVault] Loaded native Vultisig Monero fromt bundle.');
			} catch (err) {
				console.warn('[tryImportNativeVault] Native Monero fromt bundle parse failed:', err?.message || err);
			}
		}
	}

	if (frostData) {
		Object.defineProperty(vault, '_frostData', {
			value: frostData, writable: false, enumerable: false,
		});
		// Persist the unpacked bundle to localStorage under the vault UID so
		// `shouldPromptOrchardFrostSetup()` (which only consults localStorage)
		// recognises the vault as already set up — without this, freshly
		// loaded baked-in vaults trigger the OrchardFrostSetupWizard even
		// though the keys are already present in chainKeyShares.
		try {
			const pk = vault?.publicKeys?.ecdsa || vault?.data?.publicKeys?.ecdsa;
			const uid = pk ? String(pk).replace(/^0x/i, '').slice(0, 16) : '';
			if (uid.length >= 8 && frostData.orchardFrost) {
				const { storeOrchardBundle, loadOrchardBundle } = await import(
					'../components/toolbox/zcash-extensions/frozt/orchardFrostVaultIntegration'
				);
				const fileBundle = frostData.orchardFrost;
				const existing = loadOrchardBundle(uid);
				// Decide whether to replace any existing localStorage bundle.
				// We never silently replace a proper-DKG bundle — that would
				// invalidate the other devices' shares. Other than that, the
				// freshly-loaded `.vult` / `.wult` file is the user's stated
				// source of truth, so its bundle wins. This also unsticks
				// `bakedAtCreation` flags that landed in localStorage stale
				// from before the flag existed.
				const shouldReplace = !existing
					|| (existing.dkgKind !== 'proper-dkg' && (
						!existing.bakedAtCreation
						|| !!fileBundle.bakedAtCreation
						|| !!fileBundle.orchardExtras
					));
				if (shouldReplace) {
					storeOrchardBundle(uid, fileBundle);
				}
			}
		} catch (e) {
			console.warn('[tryImportNativeVault] Could not cache Orchard bundle to localStorage:', e?.message || e);
		}
	}
	if (fileCrossIdentity?.hexChainCode && fileCrossIdentity?.publicKeyEcdsa) {
		Object.defineProperty(vault, '_winbit32RecoveredIdentity', {
			value: {
				hexChainCode: fileCrossIdentity.hexChainCode,
				publicKeyHex: fileCrossIdentity.publicKeyEcdsa,
			},
			writable: true,
			enumerable: false,
			configurable: true,
		});
	}

	if (fileSaplingB64) {
		const pk = vault?.publicKeys?.ecdsa || vault?.data?.publicKeys?.ecdsa;
		const uid = pk ? String(pk).replace(/^0x/i, '').slice(0, 16) : '';
		if (uid.length >= 8) {
			try {
				const { storeSaplingBundle } = await import(
					'../components/toolbox/zcash-extensions/frozt/froztVaultIntegration'
				);
				const bin = atob(fileSaplingB64);
				const bytes = new Uint8Array(bin.length);
				for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
				storeSaplingBundle(uid, bytes);
			} catch (e) {
				console.warn('[tryImportNativeVault] Sapling bundle restore failed:', e?.message || e);
			}
		}
	}

	if (fileMoneroBundle) {
		const pk = vault?.publicKeys?.ecdsa || vault?.data?.publicKeys?.ecdsa;
		const uid = pk ? String(pk).replace(/^0x/i, '').slice(0, 16) : '';
		if (uid.length >= 8) {
			try {
				const { storeFromtBundle } = await import(
					'../components/toolbox/monero-extensions/fromt/fromtFrostVaultIntegration'
				);
				storeFromtBundle(uid, fileMoneroBundle);
			} catch (e) {
				console.warn('[tryImportNativeVault] Monero bundle restore failed:', e?.message || e);
			}
		}
		// Attach to the vault instance so callers that don't go via
		// localStorage (e.g. the export path right after import) can
		// still find the bundle without re-running the DKG.
		try {
			Object.defineProperty(vault, '_moneroFrostBundle', {
				value: fileMoneroBundle,
				writable: true,
				enumerable: false,
				configurable: true,
			});
		} catch { /* ignore */ }
	}

	// Always hydrate cross-chain identity from the key share at import time
	// so every downstream consumer has hexChainCode + ECDSA pubkey available
	// without needing a separate hydration call.
	if (!fileCrossIdentity) {
		try {
			await hydrateVaultCrossChainIdentity(vault);
		} catch (e) {
			console.warn('[tryImportNativeVault] Identity hydration failed:', e?.message || e);
		}
	}

	return vault;
}

export function isSecureVault(vault) {
	return vault?.type === 'secure' || Vultisig.isSecureVault?.(vault);
}

export function isFastVault(vault) {
	return vault?.type === 'fast' || Vultisig.isFastVault?.(vault);
}

/**
 * Runtime monkey-patch for SecureVault.signBytes.
 *
 * The SDK's signBytesWithRelay omits the CustomMessagePayload from the QR,
 * causing the mobile app to reject it.  scripts/patch-vultisig-sdk.js fixes
 * the dist files, but webpack's dev server caches node_modules and won't
 * pick up the change until restarted.
 *
 * This patch re-routes signBytes through sign(), which uses signWithRelay
 * and correctly includes a keysignPayload in the QR.  The MPC protocol
 * only needs messageHashes (not the display payload) to produce signatures.
 *
 * Applied once per SecureVault prototype; individual vault calls are no-ops.
 */
let _protoPatched = false;

export function patchSecureVaultSignBytes(_vault) {
	if (_protoPatched) return;
	if (!SecureVault?.prototype?.signBytes) return;
	_protoPatched = true;

	const _origSign = SecureVault.prototype.sign;

	SecureVault.prototype.signBytes = async function patchedSignBytes(payload, opts = {}) {
		let hex = typeof payload.data === 'string' ? payload.data : '';
		if (hex.startsWith('0x')) hex = hex.slice(2);
		hex = hex.toLowerCase();

		const signingPayload = {
			chain: payload.chain || 'ethereum',
			messageHashes: [hex],
			transaction: undefined,
		};

		try {
			return await _origSign.call(this, signingPayload, opts);
		} catch (err) {
			const msg = err?.message || '';
			if (msg.includes('cannot use field') || msg.includes('CustomMessagePayload')) {
				throw new Error(
					`signBytes relay failed (protobuf issue — restart the dev server ` +
					`to pick up the SDK patch): ${msg}`
				);
			}
			throw err;
		}
	};
}

export async function createFastVault({ name, email, password }) {
	const sdk = await initSDK();
	return sdk.createFastVault({ name, email, password });
}

export async function verifyVault(vaultId, code) {
	const sdk = await initSDK();
	return sdk.verifyVault(vaultId, code);
}

export async function createFastVaultFromSeedphrase({
	mnemonic, name, email, password, onProgress,
}) {
	const sdk = await initSDK();
	return sdk.createFastVaultFromSeedphrase({
		mnemonic, name, email, password,
		discoverChains: true,
		onProgress,
	});
}

export async function createSecureVaultFromSeedphrase({
	mnemonic, name, password, devices = 2, onProgress, onQRCodeReady, onDeviceJoined,
}) {
	const sdk = await initSDK();
	return sdk.createSecureVaultFromSeedphrase({
		mnemonic, name, password, devices,
		discoverChains: true,
		onProgress, onQRCodeReady, onDeviceJoined,
	});
}

/**
 * Create a multi-party SecureVault locally by running N SDK instances.
 * Each instance acts as a separate "device" for the DKG protocol; the
 * QR payload is passed programmatically instead of scanning.
 *
 * Accepts an optional AbortSignal (`signal`) so the caller can cancel
 * the long-running DKG process.  On abort the SDK instances are
 * best-effort cleaned up and an AbortError is thrown.
 *
 * Returns { vaults: VaultBase[], instances: Vultisig[] }
 * where vaults[i] is the share held by instances[i].
 */
let _activeCreationInstances = null;
let _activeFetchRestore = null;

/**
 * Force-abort any in-progress vault creation.
 * Disposes all SDK instances; fetch interceptor is kept active until
 * background promises settle so the SDK's retry loops get fast rejections.
 */
export function forceAbortVaultCreation() {
	if (_activeCreationInstances) {
		for (const inst of _activeCreationInstances) {
			try { inst.dispose(); } catch { /* ignore */ }
		}
		_activeCreationInstances = null;
	}
}

/**
 * Create a multi-device SecureVault entirely in this browser tab.
 *
 * Every device share is generated locally — no relay or FastVault server
 * is contacted. We achieve that by installing an in-memory MPC relay
 * (`installInMemoryMpcRelay`) that intercepts any fetch hitting
 * `https://api.vultisig.com/router` and serves it from local state, so
 * the SDK's hard-coded relay URL becomes a no-op match-maker between the
 * `Vultisig` instances we spawn.
 *
 * The interceptor is kept alive until both the initiator and joiner
 * promises settle (success or failure) so the SDK's retry loops get
 * synchronous local responses while unwinding.
 */
/**
 * Create a multi-device SecureVault entirely in this browser tab.
 *
 * Runs the SDK's DKLS24 ECDSA DKG entirely in this browser tab. When
 * `bakeOrchardFrost` is true, it also runs an Orchard FROST keygen
 * for the same {N, T}; those bundles are returned for WINBIT32 `.wult`
 * wrapping only. When `bakeMoneroFrost` is true (default: same as
 * `bakeOrchardFrost` — i.e. .wult format implies both), an Ed25519
 * FROST DKG also runs through `fromtFrostVaultIntegration` and the
 * resulting per-device Monero bundles are returned in `moneroBundles`.
 *
 * Compatible `.vult` creation runs neither overlay.
 *
 * @returns {Promise<{
 *   vaults: object[],
 *   instances: object[],
 *   orchardBundles: object[]|null,
 *   orchardBakedIn: boolean,
 *   moneroBundles: object[]|null,
 *   moneroBakedIn: boolean,
 * }>}
 */
export async function createLocalSecureVault({
	mnemonic, name, password, devices = 2, threshold = 2, onProgress, signal,
	bakeOrchardFrost,
	bakeMoneroFrost,
}) {
	forceAbortVaultCreation();
	if (_activeFetchRestore) { _activeFetchRestore(); _activeFetchRestore = null; }

	const instances = Array.from({ length: devices }, () =>
		new Vultisig({ storage: new MemoryStorage() })
	);
	_activeCreationInstances = instances;

	const disposeInstances = () => {
		for (const inst of instances) {
			try { inst.dispose(); } catch { /* ignore */ }
		}
		if (_activeCreationInstances === instances) {
			_activeCreationInstances = null;
		}
	};

	const throwIfAborted = () => {
		if (signal?.aborted) {
			disposeInstances();
			throw new DOMException('Vault creation aborted', 'AbortError');
		}
	};

	const relay = installInMemoryMpcRelay({ signal });
	const restoreFetch = () => {
		try { relay.dispose(); } catch { /* ignore */ }
		if (_activeFetchRestore === restoreFetch) _activeFetchRestore = null;
	};
	_activeFetchRestore = restoreFetch;
	if (signal) {
		signal.addEventListener('abort', () => {
			disposeInstances();
		}, { once: true });
	}

	let dkgPromise = null;

	try {
		throwIfAborted();
		await Promise.all(instances.map(inst => inst.initialize()));
		throwIfAborted();

		let qrPayload = null;
		let qrResolve, qrReject;
		const qrReady = new Promise((resolve, reject) => {
			qrResolve = resolve;
			qrReject = reject;
		});
		signal?.addEventListener('abort', () => {
			qrReject(new DOMException('Vault creation aborted', 'AbortError'));
		}, { once: true });

		// Helper: attach an immediate no-op `.catch` so that disposing
		// SDK instances mid-flight cannot surface as multiple uncaught
		// "Aborted" runtime errors in the dev overlay. The original
		// promise is still returned for downstream awaiters.
		const swallowRejection = (p) => {
			p.catch(() => { /* handled below by Promise.all / qrReject */ });
			return p;
		};

		const initiatorPromise = swallowRejection(
			instances[0].createSecureVaultFromSeedphrase({
				mnemonic, name, password, devices, threshold,
				discoverChains: false,
				signal,
				onQRCodeReady: (qr) => {
					qrPayload = qr;
					qrResolve();
				},
				onProgress,
			})
		);

		// Surface initiator failures (e.g. wasm load errors) as soon as
		// they happen instead of stranding the joiners on the QR-ready
		// wait. This also doubles as an attached rejection handler.
		initiatorPromise.catch(err => qrReject(err));

		await qrReady;
		throwIfAborted();

		const joinerPromises = instances.slice(1).map(inst =>
			swallowRejection(
				inst.joinSecureVault(qrPayload, { devices, mnemonic, signal })
			)
		);

		dkgPromise = Promise.all([initiatorPromise, ...joinerPromises]);

		// Let background promises restore fetch when they finish.
		dkgPromise.catch(() => {}).finally(() => restoreFetch());

		let results;
		if (signal && !signal.aborted) {
			const abortPromise = new Promise((_, reject) => {
				signal.addEventListener('abort', () => {
					reject(new DOMException('Vault creation aborted', 'AbortError'));
				}, { once: true });
			});
			results = await Promise.race([dkgPromise, abortPromise]);
		} else {
			throwIfAborted();
			results = await dkgPromise;
		}

		const [initiatorResult, ...joinerResults] = results;
		const vaults = [
			initiatorResult?.vault || initiatorResult,
			...joinerResults.map(r => r?.vault || r),
		];

		throwIfAborted();

		// Capture each share's cross-chain identity (BIP-32 chain code +
		// group ECDSA pubkey) NOW, while the freshly-DKG'd key shares are
		// still decrypted in memory. This guarantees `exportVaultWithFrost`
		// can embed `crossChainIdentity` in every `.wult` (and that the
		// in-memory vaults auto-activated into Money Manager carry it too).
		// Without it, a password-locked reload can't read the chain code
		// and the Zcash/Monero wizards fail with "Cannot read cross-chain
		// metadata".
		for (const v of vaults) {
			try {
				const ok = await hydrateVaultCrossChainIdentity(v);
				if (!ok) {
					console.warn(
						'[createLocalSecureVault] Could not capture cross-chain identity for share',
						v?.coreVault?.localPartyId || v?.localPartyId || '(unknown)',
						'— exported file may need "Recover from loaded share" on reload.',
					);
				}
			} catch (e) {
				console.warn('[createLocalSecureVault] cross-chain identity capture failed:', e?.message || e);
			}
		}

		// Orchard FROST keygen — only for the WINBIT32 `.wult` path.
		// Compatible `.vult` exports must not generate private Orchard
		// material that will not be saved anywhere durable.
		let orchardBundles = null;
		let orchardBakedIn = Boolean(bakeOrchardFrost);
		if (orchardBakedIn) {
			const {
				generateOrchardBundlesForFreshVault,
			} = await import(
				'../components/toolbox/zcash-extensions/frozt/orchardFrostVaultIntegration'
			);
			try {
				if (typeof onProgress === 'function') {
					onProgress({ progress: 0.92, message: 'Generating Orchard FROST key shares...' });
				}
				const partyIds = vaults
					.map(v => v?.coreVault?.localPartyId)
					.filter(Boolean);
				orchardBundles = await generateOrchardBundlesForFreshVault({
					devices,
					minSigners: threshold,
					participantPartyIds: partyIds.length === devices ? partyIds : undefined,
				});
				throwIfAborted();
				for (let i = 0; i < vaults.length; i++) {
					if (!orchardBundles[i]) continue;
					Object.defineProperty(vaults[i], '_frostData', {
						value: { orchardFrost: orchardBundles[i] },
						writable: false,
						enumerable: false,
						configurable: true,
					});
				}
				if (typeof onProgress === 'function') {
					onProgress({
						progress: 0.94,
						message: 'Orchard FROST shares prepared for WINBIT32 .wult files.',
					});
				}
			} catch (err) {
				if (err?.name === 'AbortError') throw err;
				// Loud + structured so the Create Vault Wizard's
				// "Orchard FROST bake-in failed" warning panel has a
				// matching console line for support / triage.
				console.error(
					'[createLocalSecureVault] Orchard FROST preparation failed; .wult export will not include Orchard shares:',
					err,
				);
				orchardBundles = null;
				orchardBakedIn = false;
			}
		}

		// Monero (fromt) FROST keygen. Defaults to "same as Orchard"
		// — if the user picked `.wult`, they get the full privacy
		// bundle baked in. Callers can opt out (or in) explicitly by
		// passing `bakeMoneroFrost` separately. Failures degrade
		// gracefully exactly like Orchard.
		const wantMonero = bakeMoneroFrost === undefined
			? Boolean(bakeOrchardFrost)
			: Boolean(bakeMoneroFrost);
		let moneroBundles = null;
		let moneroBakedIn = wantMonero;
		if (moneroBakedIn) {
			try {
				if (typeof onProgress === 'function') {
					onProgress({ progress: 0.95, message: 'Generating Monero FROST key shares...' });
				}
				const { generateFromtBundlesForFreshVault } = await import(
					'../components/toolbox/monero-extensions/fromt/fromtFrostVaultIntegration'
				);
				const { approximateHeightForDate } = await import(
					'./monero/moneroBlockHeight'
				);
				const partyIds = vaults
					.map(v => v?.coreVault?.localPartyId)
					.filter(Boolean);
				// Stamp today's approximate chain tip as the bundle
				// birthday so a freshly created vault doesn't ask LWS
				// to rescan the entire chain on first use.
				const freshBirthday = BigInt(Math.max(0, Math.floor(approximateHeightForDate(new Date()))));
				moneroBundles = await generateFromtBundlesForFreshVault({
					devices,
					minSigners: threshold,
					participantPartyIds: partyIds.length === devices ? partyIds : undefined,
					birthday: freshBirthday,
					signal,
				});
				throwIfAborted();
				for (let i = 0; i < vaults.length; i++) {
					if (!moneroBundles[i]) continue;
					try {
						Object.defineProperty(vaults[i], '_moneroFrostBundle', {
							value: moneroBundles[i],
							writable: true,
							enumerable: false,
							configurable: true,
						});
					} catch { /* ignore */ }
				}
				if (typeof onProgress === 'function') {
					onProgress({
						progress: 0.97,
						message: 'Monero FROST shares prepared for WINBIT32 .wult files.',
					});
				}
			} catch (err) {
				if (err?.name === 'AbortError') throw err;
				console.error(
					'[createLocalSecureVault] Monero FROST preparation failed; .wult export will not include Monero shares:',
					err,
				);
				moneroBundles = null;
				moneroBakedIn = false;
			}
		}

		if (_activeCreationInstances === instances) {
			_activeCreationInstances = null;
		}
		return {
			vaults,
			instances,
			orchardBundles,
			orchardBakedIn,
			moneroBundles,
			moneroBakedIn,
		};
	} catch (err) {
		disposeInstances();
		if (!dkgPromise) {
			restoreFetch();
		}
		throw err;
	}
}

export async function importVault(file, password) {
	const sdk = await initSDK();
	const isEncrypted = await sdk.isVaultFileEncrypted(file);
	return sdk.addVault(file, isEncrypted ? password : undefined);
}

export async function validateSeedphrase(mnemonic) {
	const sdk = await initSDK();
	return sdk.validateSeedphrase(mnemonic);
}

export async function deriveAddresses(vault, appChains) {
	const results = [];
	const seenChains = new Set();

	for (const appChain of appChains) {
		const candidates = resolveVultisigChainCandidates(appChain);
		if (candidates.length === 0) {
			console.debug(`deriveAddresses: no Vultisig mapping for chain "${appChain}"`);
			continue;
		}

		for (const vsChain of candidates) {
			try {
				const address = await vault.address(vsChain);
				if (address) {
					results.push({ chain: appChain, address });
					seenChains.add(appChain);
					break;
				}
			} catch (err) {
				console.debug(`deriveAddresses: failed for ${appChain} (${vsChain}):`, err?.message || err);
			}
		}
	}

	// Fallback: ask SDK for all available addresses when direct resolution fails.
	if (results.length === 0 && typeof vault.addresses === 'function') {
		try {
			const all = await vault.addresses();
			const reverseMap = Object.fromEntries(
				Object.entries(VULTISIG_CHAIN_MAP).map(([appChain, vsChain]) => [String(vsChain), appChain]),
			);
			for (const [vsChain, address] of Object.entries(all || {})) {
				const appChain = reverseMap[vsChain];
				if (!appChain || !address || seenChains.has(appChain)) continue;
				if (appChains.includes(appChain)) {
					results.push({ chain: appChain, address });
					seenChains.add(appChain);
				}
			}
		} catch (err) {
			console.debug('deriveAddresses: vault.addresses() fallback failed:', err?.message || err);
		}
	}

	console.log(`deriveAddresses: derived ${results.length} addresses from ${appChains.length} chains`);
	return results;
}

export async function exportVault(vault, password) {
	return vault.export(password);
}

export async function exportVaultBase64(vault, password) {
	return vault.exportAsBase64(password);
}

// ── WINBIT32 Vault Wrapper (embeds FROST key shares in .wult files) ──
//
// The on-disk JSON envelope and its (un)wrapping helpers live in
// `vaultWrapperFormat.js` — that module is SDK-free so it can be
// unit-tested in isolation. We re-export them here so existing
// callers (`import { wrapVaultWithFrost, ... } from '../services/vultisig'`)
// keep working.
//
// `VULTISIG_ORCHARD_CHAIN` / `VULTISIG_MONERO_CHAIN` are the chain
// labels the official Vultisig SDK uses (will use, in Monero's case)
// for `chainKeyShares[…]` — kept here next to the import code that
// actually pokes at those slots.

const VULTISIG_ORCHARD_CHAIN = 'ZcashOrchard';
// Frosty-lib's Monero FROST keyshare slot. Mirrors the Orchard layout: when
// the official Vultisig SDK starts emitting `chainKeyShares['Monero']` we
// can read it directly; until then the share lives in the WINBIT32 `.wult`
// JSON envelope under `frost.monero`.
const VULTISIG_MONERO_CHAIN = 'Monero';

export const isWrappedVault = _isWrappedVault;
export const wrapVaultWithFrost = _wrapVaultWithFrost;
export const unwrapVault = _unwrapVault;

/**
 * Export a vault. If Orchard/Sapling FROST material is present, use the
 * WINBIT32 `.wult` wrapper instead of mutating the native Vultisig
 * protobuf. Official Vultisig currently tolerates dedicated FROZT public
 * key fields, but arbitrary private keyshare blobs in `chainKeyShares`
 * make its parser fail with `illegal tag: field no 678 wire type 7`.
 *
 * @param {object} vault  SDK vault instance
 * @param {string|undefined} password  vault encryption password (optional)
 * @param {object|null} orchardFrost  { myShare, publicKeyPackage } for this share
 * @returns {Promise<{ filename: string, data: string }>}
 */
/**
 * Export a Vultisig SecureVault, optionally wrapped in the WINBIT32
 * `.wult` JSON envelope.
 *
 * Wrapping is decided in this priority order:
 *   1. `options.forceWrap === true`  → always produce `.wult`. Use this
 *      when the *user* explicitly picked the `.wult` format in the
 *      Create Vault Wizard, even if Orchard bake-in failed: the
 *      wrapper is still useful (it carries the cross-chain identity
 *      block), and silently degrading to `.vult` would lie about the
 *      user's choice.
 *   2. `options.forceWrap === false` → always produce `.vult`. Used
 *      by the wizard's Convert tool when stripping FROST extras.
 *   3. Default (`forceWrap === undefined`): wrap only if there is FROST
 *      data to carry (orchard/sapling/monero bundle), otherwise hand
 *      back the raw SDK export. Preserves the historic "auto" mode.
 *
 * @param {*} vault             SDK vault instance
 * @param {string=} password    encryption password (matches the SDK export)
 * @param {object=} orchardFrost OrchardKeyBundle to embed (optional)
 * @param {{ forceWrap?: boolean, moneroFrost?: object|null }} [options]
 *   - `moneroFrost`: explicit MoneroKeyShareBundle to embed. When
 *     omitted, the bundle is resolved from `vault._moneroFrostBundle`
 *     (set by `tryImportNativeVault` / `createLocalSecureVault`) or
 *     `loadFromtBundle(vaultUid)` from localStorage — same precedence
 *     as the Orchard side via `_frostData`.
 */
export async function exportVaultWithFrost(vault, password, orchardFrost, options = {}) {
	const { forceWrap, moneroFrost: explicitMonero } = options;

	// Export FIRST. `.export(password)` decrypts/serialises the key shares
	// with the supplied password, so the cross-chain identity recovery
	// below can read the ECDSA share + chain code even for a freshly
	// created, password-locked vault. Recovering BEFORE export was the
	// root of the ".wult created here can't be reopened — Cannot read
	// cross-chain metadata (chain code + ECDSA public key)" bug: the
	// fresh vault's shares weren't decrypted yet, identity recovery
	// returned null, and the wrapper shipped without `crossChainIdentity`.
	const sdkExport = await vault.export(password);
	const sdkData = typeof sdkExport === 'string' ? sdkExport : sdkExport?.data;
	if (!sdkData) throw new Error('Vault export produced no data');

	await hydrateVaultCrossChainIdentity(vault);
	const picked = pickCrossChainIdentityFromVaultObject(vault);
	const crossChainIdentity = picked
		? { hexChainCode: picked.hexChainCode, publicKeyEcdsa: picked.publicKeyHex }
		: null;

	let saplingBundleBase64 = null;
	const pk = vault?.publicKeys?.ecdsa || vault?.data?.publicKeys?.ecdsa;
	const vaultUid = pk ? String(pk).replace(/^0x/i, '').slice(0, 16) : '';
	if (vaultUid.length >= 8) {
		try {
			const { loadSaplingBundle } = await import(
				'../components/toolbox/zcash-extensions/frozt/froztVaultIntegration'
			);
			const sap = loadSaplingBundle(vaultUid);
			if (sap && sap.length) {
				saplingBundleBase64 = btoa(String.fromCharCode(...sap));
			}
		} catch (e) {
			console.warn('[exportVaultWithFrost] Sapling bundle attach skipped:', e?.message || e);
		}
	}

	// Resolve Monero FROST bundle from (in priority order): the explicit
	// option, the vault's in-memory attachment, then the per-vault
	// localStorage cache. This matches how Orchard data flows in.
	let moneroFrost = explicitMonero || vault?._moneroFrostBundle || null;
	if (!moneroFrost && vaultUid.length >= 8) {
		try {
			const { loadFromtBundle } = await import(
				'../components/toolbox/monero-extensions/fromt/fromtFrostVaultIntegration'
			);
			moneroFrost = loadFromtBundle(vaultUid) || null;
		} catch (e) {
			console.warn('[exportVaultWithFrost] Monero bundle attach skipped:', e?.message || e);
		}
	}

	const hasFrostExtras = !!orchardFrost || !!saplingBundleBase64 || !!moneroFrost;
	const shouldWrap = forceWrap === true
		|| (forceWrap !== false && hasFrostExtras);

	if (!shouldWrap) {
		const sdkFilename = typeof sdkExport === 'object' ? sdkExport.filename : null;
		return { filename: sdkFilename || 'vault.vult', data: sdkData };
	}

	// A `.wult` without cross-chain identity can't derive Zcash/Monero
	// addresses on reload without a "Recover from loaded share" step. This
	// should never happen now that we export before recovering, so make a
	// regression loud rather than silently shipping a half-broken file.
	if (!crossChainIdentity) {
		console.error(
			'[exportVaultWithFrost] Producing a .wult WITHOUT cross-chain identity '
			+ '(chain code + ECDSA pubkey). Zcash/Monero derivation will need '
			+ '"Recover from loaded share" on reload — the vault key shares could '
			+ 'not be decrypted/read at export time.',
		);
	}

	const wrapped = await wrapVaultWithFrost({
		sdkExportData: sdkData,
		orchardFrost,
		moneroFrost,
		password,
		crossChainIdentity,
		saplingBundleBase64,
	});
	// The `.wult` wrapper is a JSON object around the SDK's base64
	// export; Vultisig itself cannot load it directly. Keep the
	// distinct `.wult` extension so users don't try to open the file
	// in the Vultisig app and get a confusing import error.
	const sdkFilename = typeof sdkExport === 'object' ? sdkExport.filename : null;
	let filename = 'vault.wult';
	if (sdkFilename) {
		filename = sdkFilename.replace(/\.vult$/i, '.wult');
		if (!/\.wult$/i.test(filename)) filename += '.wult';
	}
	return { filename, data: wrapped };
}

/**
 * Export the active vault as a WINBIT32 `winbit32-vault-v2` JSON file: SDK share,
 * optional Orchard FROST from local storage, Sapling frozt bundle if present,
 * and cross-chain identity (chain code + ECDSA pubkey) for Zcash/Radix derivation.
 *
 * @param {string|undefined} windowId
 * @param {string|undefined} password  same password used for vault encryption
 * @returns {Promise<{ filename: string, data: string }>}
 */
export async function exportActiveVaultWinbit32Bundle(windowId, password) {
	const { vault: v } = _findActiveVaultAnyScope(windowId);
	if (!v) throw new Error('No active vault loaded.');
	const uid = getVaultUid(windowId);
	let orchardFrost = null;
	let moneroFrost = null;
	if (uid) {
		const { loadOrchardBundle } = await import(
			'../components/toolbox/zcash-extensions/frozt/orchardFrostVaultIntegration'
		);
		orchardFrost = loadOrchardBundle(uid);
		try {
			const { loadFromtBundle } = await import(
				'../components/toolbox/monero-extensions/fromt/fromtFrostVaultIntegration'
			);
			moneroFrost = loadFromtBundle(uid) || null;
		} catch (e) {
			console.warn('[exportActiveVaultWinbit32Bundle] Monero bundle lookup skipped:', e?.message || e);
		}
	}
	return exportVaultWithFrost(v, password, orchardFrost, { moneroFrost });
}

/**
 * Re-wrap an existing vault file with new Orchard FROST data (upgrade path).
 * Does NOT require a new ECDSA/EdDSA DKG — only generates FROST shares.
 *
 * @param {string} vaultContent  raw .vult file text (v1 or v2)
 * @param {string|undefined} password
 * @param {object} orchardFrost  { myShare, publicKeyPackage }
 * @returns {Promise<string>} new v2 vault file text
 */
export async function upgradeVaultFileWithFrost(vaultContent, password, orchardFrost, options = {}) {
	let sdkString = vaultContent;
	let preservedCross = null;
	let preservedSapling = null;
	let preservedMonero = null;
	if (isWrappedVault(vaultContent)) {
		const unwrapped = await unwrapVault(vaultContent, password);
		sdkString = unwrapped.sdkVaultString;
		if (unwrapped.crossChainIdentity) preservedCross = unwrapped.crossChainIdentity;
		if (unwrapped.saplingBundleBase64) preservedSapling = unwrapped.saplingBundleBase64;
		if (unwrapped.moneroFrost) preservedMonero = unwrapped.moneroFrost;
	}
	// Allow callers to override / add a Monero bundle while upgrading,
	// matching the orchardFrost positional arg pattern. Falls back to
	// whatever the file already carried.
	const moneroFrost = options.moneroFrost !== undefined
		? options.moneroFrost
		: preservedMonero;
	return wrapVaultWithFrost({
		sdkExportData: sdkString,
		orchardFrost,
		moneroFrost,
		password,
		crossChainIdentity: preservedCross,
		saplingBundleBase64: preservedSapling,
	});
}

/**
 * Check whether both vault instances have loaded key shares for the
 * given algorithm and can therefore perform a fully-local MPC sign.
 */
export function canSignLocally(algorithm = 'ecdsa', windowId) {
	const scope = _getScope(windowId);
	if (!scope.activeVault) return false;
	if (scope.additionalVaults.length === 0) return false;
	try {
		const c1 = scope.activeVault.coreVault;
		const c2 = scope.additionalVaults[0].coreVault;
		return !!(c1?.keyShares?.[algorithm] && c2?.keyShares?.[algorithm]);
	} catch {
		return false;
	}
}

/**
 * Async version — calls ensureKeySharesLoaded() on both vaults first so that
 * lazy-loaded key shares are available for the synchronous check.
 */
export async function ensureCanSignLocally(algorithm = 'ecdsa', windowId) {
	const scope = _getScope(windowId);
	if (!scope.activeVault || scope.additionalVaults.length === 0) return false;
	try {
		if (typeof scope.activeVault.ensureKeySharesLoaded === 'function') {
			await scope.activeVault.ensureKeySharesLoaded();
		}
		if (typeof scope.additionalVaults[0].ensureKeySharesLoaded === 'function') {
			await scope.additionalVaults[0].ensureKeySharesLoaded();
		}
		return canSignLocally(algorithm, windowId);
	} catch {
		return false;
	}
}

function hexToBytes(hex) {
	const clean = hex.startsWith('0x') ? hex.slice(2) : hex;
	const bytes = new Uint8Array(clean.length / 2);
	for (let i = 0; i < bytes.length; i++) {
		bytes[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
	}
	return bytes;
}

function bytesToHex(bytes) {
	return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

// Native Monero KeyShareBundle in `chainKeyShares['Monero']` is the raw
// binary frosty-lib bundle. The SDK protobuf decoder hands it back to
// us either as a base64 string (most likely, mirroring `ZcashOrchard`)
// or as a Uint8Array / number[] depending on transport. Normalise to
// bytes for `parseKeyShareBundleHeader`.
function _moneroNativeBundleToBytes(native) {
	if (native instanceof Uint8Array) return native;
	if (Array.isArray(native)) return new Uint8Array(native);
	if (typeof native === 'string') {
		try {
			const bin = atob(native);
			const out = new Uint8Array(bin.length);
			for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
			return out;
		} catch {
			// Fall through — caller logs the failure.
		}
	}
	if (native?.data) return _moneroNativeBundleToBytes(native.data);
	throw new Error('Unrecognised Monero chainKeyShares payload shape');
}

function _networkNameFromByte(byte) {
	switch (byte) {
		case 0x12: return 'mainnet';
		case 0x35: return 'testnet';
		case 0x18: return 'stagenet';
		default: return 'mainnet';
	}
}

function encodeDERSignatureLocal(r, s) {
	const pad = (buf) => (buf[0] & 0x80) ? new Uint8Array([0, ...buf]) : buf;
	const rPad = pad(r);
	const sPad = pad(s);
	const total = 2 + rPad.length + 2 + sPad.length;
	const der = new Uint8Array(2 + total);
	let offset = 0;
	der[offset++] = 0x30;
	der[offset++] = total;
	der[offset++] = 0x02;
	der[offset++] = rPad.length;
	der.set(rPad, offset); offset += rPad.length;
	der[offset++] = 0x02;
	der[offset++] = sPad.length;
	der.set(sPad, offset);
	return der;
}

const MAX_MPC_ROUNDS = 1000;

/**
 * Perform a fully-local two-party MPC sign using the DKLS (ECDSA) or
 * Schnorr (EdDSA) WASM primitives.  No relay server is involved.
 *
 * @param {object} opts
 * @param {string} opts.messageHash  Hex-encoded hash to sign (with or without 0x prefix)
 * @param {string} opts.chainPath    BIP-32 derivation path, e.g. "m/44/60/0/0/0"
 * @param {'ecdsa'|'eddsa'} [opts.algorithm='ecdsa']
 * @returns {Promise<{signature: string, recovery?: number, format: string, r: string, s: string}>}
 */
export async function localMPCSign({ messageHash, chainPath, algorithm = 'ecdsa', windowId }) {
	const { windowId: resolvedId } = _findActiveVaultAnyScope(windowId);
	const scope = _getScope(resolvedId);
	if (!scope.activeVault || scope.additionalVaults.length === 0) {
		throw new Error('Local MPC sign requires at least two loaded vault shares.');
	}

	const vault1 = scope.activeVault;
	const vault2 = scope.additionalVaults[0];

	// Ensure key shares are decrypted and loaded into coreVault
	if (typeof vault1.ensureKeySharesLoaded === 'function') {
		await vault1.ensureKeySharesLoaded();
	}
	if (typeof vault2.ensureKeySharesLoaded === 'function') {
		await vault2.ensureKeySharesLoaded();
	}

	const core1 = vault1.coreVault;
	const core2 = vault2.coreVault;

	const ks1 = core1?.keyShares?.[algorithm];
	const ks2 = core2?.keyShares?.[algorithm];
	if (!ks1 || !ks2) {
		throw new Error(`Missing ${algorithm} key share in one or both vault instances.`);
	}

	// Initialise WASM (no-op if already initialised by the SDK)
	if (algorithm === 'ecdsa') {
		await initDkls();
	} else {
		await initSchnorr();
	}

	const KeyshareClass = algorithm === 'ecdsa' ? DklsKeyshare : SchnorrKeyshare;
	const SessionClass = algorithm === 'ecdsa' ? DklsSignSession : SchnorrSignSession;

	// Deserialise the key shares (base64 → bytes → WASM Keyshare)
	const keyshare1 = KeyshareClass.fromBytes(Uint8Array.from(atob(ks1), c => c.charCodeAt(0)));
	const keyshare2 = KeyshareClass.fromBytes(Uint8Array.from(atob(ks2), c => c.charCodeAt(0)));

	const partyId1 = core1.localPartyId;
	const partyId2 = core2.localPartyId;
	const keyId = keyshare1.keyId();

	const msgBytes = hexToBytes(messageHash);

	// Normalise chainPath: strip quotes, ensure no leading "m/"
	const normPath = chainPath.replace(/'/g, '').replace(/^m\//, 'm/');

	const setupBytes = SessionClass.setup(keyId, normPath, msgBytes, [partyId1, partyId2]);

	const session1 = new SessionClass(setupBytes, partyId1, keyshare1);
	const session2 = new SessionClass(setupBytes, partyId2, keyshare2);

	let round = 0;
	let finished1 = false;
	let finished2 = false;

	while (!(finished1 && finished2) && round < MAX_MPC_ROUNDS) {
		round++;
		let progress = false;

		const msg1 = session1.outputMessage();
		if (msg1) {
			progress = true;
			if (session2.inputMessage(msg1.body)) finished2 = true;
		}

		const msg2 = session2.outputMessage();
		if (msg2) {
			progress = true;
			if (session1.inputMessage(msg2.body)) finished1 = true;
		}

		if (!progress && !(finished1 && finished2)) {
			await new Promise(r => setTimeout(r, 1));
		}
	}

	if (round >= MAX_MPC_ROUNDS) {
		throw new Error('Local MPC sign: protocol did not converge within iteration limit.');
	}

	const sigBytes = session1.finish();

	const rawR = sigBytes.slice(0, 32);
	const rawS = sigBytes.slice(32, 64);

	const r = algorithm === 'eddsa'
		? bytesToHex(Array.from(rawR).reverse())
		: bytesToHex(rawR);
	const s = algorithm === 'eddsa'
		? bytesToHex(Array.from(rawS).reverse())
		: bytesToHex(rawS);

	const recoveryId = algorithm === 'ecdsa' && sigBytes.length > 64
		? sigBytes[64]
		: undefined;

	const derHex = algorithm === 'ecdsa'
		? bytesToHex(encodeDERSignatureLocal(rawR, rawS))
		: undefined;

	return {
		signature: algorithm === 'eddsa' ? (r + s) : derHex,
		recovery: recoveryId,
		format: algorithm === 'ecdsa' ? 'ECDSA' : 'EdDSA',
		r,
		s,
		recoveryId: recoveryId !== undefined ? recoveryId.toString(16).padStart(2, '0') : undefined,
		derSignature: derHex,
		rawSignatureBytes: new Uint8Array(sigBytes),
	};
}

/**
 * Export the vault's master ECDSA private key by running the DKLS
 * KeyExportSession protocol locally with two loaded shares.
 *
 * Security: This reconstructs the full private key in the browser.
 * Only call when the user has explicitly requested it and understands
 * that the key controls all vault funds across every chain.
 *
 * @returns {Promise<string>} Hex-encoded 32-byte secp256k1 private key
 */
export async function exportVaultMasterPrivateKey(windowId) {
	const scope = _getScope(windowId);
	if (!scope.activeVault || scope.additionalVaults.length === 0) {
		throw new Error('Master key export requires at least two loaded vault shares.');
	}

	const vault1 = scope.activeVault;
	const vault2 = scope.additionalVaults[0];

	if (typeof vault1.ensureKeySharesLoaded === 'function') {
		await vault1.ensureKeySharesLoaded();
	}
	if (typeof vault2.ensureKeySharesLoaded === 'function') {
		await vault2.ensureKeySharesLoaded();
	}

	const core1 = vault1.coreVault;
	const core2 = vault2.coreVault;

	const ks1 = core1?.keyShares?.ecdsa;
	const ks2 = core2?.keyShares?.ecdsa;
	if (!ks1 || !ks2) {
		throw new Error('Missing ECDSA key share in one or both vault instances.');
	}

	await initDkls();

	const keyshare1 = DklsKeyshare.fromBytes(Uint8Array.from(atob(ks1), c => c.charCodeAt(0)));
	const keyshare2 = DklsKeyshare.fromBytes(Uint8Array.from(atob(ks2), c => c.charCodeAt(0)));

	const partyId1 = core1.localPartyId;
	const partyId2 = core2.localPartyId;

	const session = new DklsKeyExportSession(keyshare1, [partyId1, partyId2]);
	try {
		const setup = session.setup;
		const shareMsg = DklsKeyExportSession.exportShare(setup, partyId2, keyshare2);
		const done = session.inputMessage(shareMsg.body);
		if (!done) {
			throw new Error('Key export protocol did not complete in a single round.');
		}
		const secretKey = session.finish();
		return bytesToHex(secretKey);
	} finally {
		session.free();
	}
}

export { VULTISIG_CHAIN_MAP };
